/**
 * One showcase picked by what this buyer has shown interest in, for a slot that
 * would otherwise be empty -- the product page's "Other items from {seller}"
 * row when the seller has too few other listings to fill one.
 *
 * The buyer's strongest tag, from the same source as For You
 * (/api/recommendations/tag-showcases): by account when signed in, by browser
 * session otherwise. A buyer with no history yet gets nothing back, and the
 * caller falls to its next choice.
 *
 * Fetches only while `enabled`, and once per listing.
 */
import { useEffect, useState } from 'react';
import { useUser } from '../contexts';
import { getAnonymousId, getSessionId } from '../utils/tracking-context';
import { isForeignRefusal, parseJsonResponse } from '../utils/read-json-response';
import { trackError, ErrorSeverity } from '../utils/error-tracking';
import type { TagShowcase } from '../schemas/api/recommendations/tag-showcases';
import { appendPageLoadAt, appendShuffleSeed } from '../utils/seeded-shuffle';
import { asError } from './as-error';

/** Cards to ask for, and the fewest that still make a row worth showing. */
const LISTINGS_PER_ROW = 12;
const MIN_LISTINGS = 4;

export interface AffinityShowcaseState {
  showcase: TagShowcase | null;
  /** True once the lookup has answered, found or not. */
  loaded: boolean;
}

export function useAffinityShowcase(enabled: boolean, listingId: number): AffinityShowcaseState {
  const { loggedIn, profile } = useUser();
  const [state, setState] = useState<AffinityShowcaseState & { forListing: number | null }>({
    showcase: null,
    loaded: false,
    forListing: null,
  });

  const userId = loggedIn && profile?.id ? String(profile.id) : '';

  useEffect(() => {
    if (!enabled || state.forListing === listingId) return;
    let cancelled = false;

    const load = async () => {
      let showcase: TagShowcase | null = null;
      try {
        const params = new URLSearchParams({
          type: userId ? 'user' : 'session',
          limit: '1',
          listingsPerTag: String(LISTINGS_PER_ROW),
          minListingsPerTag: String(MIN_LISTINGS),
        });
        if (userId) params.set('userId', userId);
        const sessionId = getSessionId();
        const anonymousId = getAnonymousId();
        if (sessionId) params.set('sessionId', sessionId);
        if (anonymousId) params.set('anonymousId', anonymousId);

        const response = await fetch(`/api/recommendations/tag-showcases?${appendPageLoadAt(appendShuffleSeed(params))}`);
        const data = await parseJsonResponse<{ success?: boolean; showcases?: TagShowcase[] }>(
          response,
          'GET /api/recommendations/tag-showcases',
        );
        const first = response.ok && data.success ? data.showcases?.[0] : undefined;
        // The listing being viewed is not a recommendation for itself.
        const listings = (first?.listings ?? []).filter(
          (l) => Number((l as { id?: unknown }).id) !== listingId,
        );
        if (first && listings.length >= MIN_LISTINGS) showcase = { ...first, listings };
      } catch (error) {
        // A missing recommendation is not worth an error page; the caller
        // falls back. Recorded so a broken endpoint is still visible.
        // A refusal from the visitor's own network (no Vercel header) is info.
        const severity = isForeignRefusal(error) ? ErrorSeverity.INFO : ErrorSeverity.WARNING;
        trackError(asError(error), severity, {
          component: 'useAffinityShowcase',
          action: 'load',
        });
      }
      if (!cancelled) setState({ showcase, loaded: true, forListing: listingId });
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [enabled, listingId, userId, state.forListing]);

  return state.forListing === listingId
    ? { showcase: state.showcase, loaded: state.loaded }
    : { showcase: null, loaded: false };
}
